import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthenticatedLayout } from "@/components/layout/AuthenticatedLayout";
import {
  MarketplaceNavbar,
  ProductCard,
  EmptyProductsView,
  AddProductModal,
} from "./components";
import {
  marketplaceApi,
  Product,
  ProductListParams,
} from "@/services/marketplace";

export const MarketplacePage: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState<"all" | "yours">("all");
  const [showAddModal, setShowAddModal] = useState(false);
  const [filters, setFilters] = useState<Record<string, unknown>>({});

  // Load products
  const loadProducts = useCallback(async () => {
    try {
      setLoading(true);
      const params: ProductListParams = {
        search: searchQuery || undefined,
        userProducts: activeTab === "yours",
        // Apply any filters here if needed
        ...filters,
      };

      const response = await marketplaceApi.getProducts(params);
      setProducts(response.results);
    } catch (error) {
      console.error("Error loading products:", error);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, activeTab, filters]);

  // Load products when search, tab, or filters change
  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const handleProductClick = (product: Product) => {
    navigate(`/marketplace/products/${product.id}`);
  };

  const handleFilterApply = (newFilters: Record<string, unknown>) => {
    setFilters(newFilters);
  };

  const handleAddProduct = () => {
    setShowAddModal(true);
  };

  const handleProductAdded = (productData: {
    name: string;
    price: string;
    location: string;
    image?: string;
    imageUrl?: string;
  }) => {
    setShowAddModal(false);
    // Reload products to show the new one
    loadProducts();
    console.log("Product added:", productData);
  };

  return (
    <div className="min-h-screen bg-[#FBF6F1]">
      <AuthenticatedLayout>
        <div className="max-w-md mx-auto">
          <MarketplaceNavbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            activeTab={activeTab}
            onTabChange={(tab) => setActiveTab(tab as "all" | "yours")}
            onFilterApply={handleFilterApply}
          />

          <div className="px-4 pb-20">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1E3A3A]"></div>
              </div>
            ) : products.length === 0 ? (
              <EmptyProductsView onAddProduct={handleAddProduct} />
            ) : (
              <>
                {/* Show add product button for user's products tab */}
                {activeTab === "yours" && (
                  <div className="mb-4">
                    <button
                      onClick={handleAddProduct}
                      className="w-full p-4 bg-[#E06448] text-white rounded-lg font-medium font-sora text-sm"
                    >
                      Thêm sản phẩm mới
                    </button>
                  </div>
                )}

                {/* Products grid */}
                <div className="grid grid-cols-2 gap-3">
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onClick={() => handleProductClick(product)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Add Product Modal */}
        <AddProductModal
          isOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          onAddProduct={handleProductAdded}
        />
      </AuthenticatedLayout>
    </div>
  );
};

export default MarketplacePage;
